import React, { useState, useEffect, useRef } from 'react';
import { AlertTriangle, Clock, MapPin, Phone, User, ShieldAlert, RefreshCw, X, Send, MessageSquare, Star, Mail } from 'lucide-react';
import { apiRequest } from '../utils/api';

const Emergencies = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal and Chat state
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [statusValue, setStatusValue] = useState('');

  const chatEndRef = useRef(null);

  const fetchEmergencies = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const response = await apiRequest('/api/admin/emergencies');
      if (!response.ok) {
        throw new Error('Erreur lors du chargement des alertes SOS');
      }
      const data = await response.json();
      setAlerts(data);
      setError(null);
      
      // Update selected alert if it is currently open
      if (selectedAlert) {
        const updated = data.find(a => a.id === selectedAlert.id);
        if (updated) {
          setSelectedAlert(updated);
        }
      }
    } catch (err) {
      console.error(err);
      setError('Impossible de récupérer la liste des alertes SOS.');
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmergencies(true);
    const interval = setInterval(() => fetchEmergencies(false), 10000);
    return () => clearInterval(interval);
  }, [selectedAlert?.id]);

  // Fetch chat messages
  const fetchChatMessages = async (alertId, quiet = false) => {
    if (!quiet) setChatLoading(true);
    try {
      const response = await apiRequest(`/api/admin/emergencies/${alertId}/messages`);
      if (response.ok) {
        const data = await response.json();
        setChatMessages(data);
      }
    } catch (err) {
      console.error('Error fetching chat messages:', err);
    } finally {
      if (!quiet) setChatLoading(false);
    }
  };

  // Poll chat messages while modal is open
  useEffect(() => {
    if (!selectedAlert) {
      setChatMessages([]);
      return;
    }

    setStatusValue(selectedAlert.status);
    fetchChatMessages(selectedAlert.id);

    const interval = setInterval(() => {
      fetchChatMessages(selectedAlert.id, true);
    }, 3000); // refresh chat every 3 seconds

    return () => clearInterval(interval);
  }, [selectedAlert?.id]);

  // Scroll chat to bottom when messages update
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const handleOpenAlert = (alert) => {
    setSelectedAlert(alert);
  };

  const handleCloseAlert = () => {
    setSelectedAlert(null);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sendingMessage || !selectedAlert) return;

    setSendingMessage(true);
    try {
      const response = await apiRequest(`/api/admin/emergencies/${selectedAlert.id}/message`, {
        method: 'POST',
        body: JSON.stringify({ content: newMessage.trim() })
      });

      if (response.ok) {
        setNewMessage('');
        fetchChatMessages(selectedAlert.id, true);
      } else {
        alert('Erreur lors de l\'envoi du message');
      }
    } catch (err) {
      console.error(err);
      alert('Impossible d\'envoyer le message.');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedAlert) return;
    setStatusValue(newStatus);
    try {
      const response = await apiRequest(`/api/admin/emergencies/${selectedAlert.id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        const updatedAlert = await response.json();
        setSelectedAlert(updatedAlert);
        setStatusValue(updatedAlert.status);
        fetchEmergencies(false);
      } else {
        alert('Erreur lors du changement de statut');
      }
    } catch (err) {
      console.error(err);
      alert('Impossible de mettre à jour le statut.');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Date inconnue';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch (e) {
      return dateStr;
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Alertes d'Urgence (SOS)</h1>
          <p style={styles.subtitle}>Supervision en temps réel et historique des signalements de détresse passagers</p>
        </div>
        <button 
          onClick={() => fetchEmergencies(true)} 
          style={styles.refreshBtn}
          className="btn-secondary"
        >
          <RefreshCw size={16} />
          <span>Rafraîchir</span>
        </button>
      </div>

      {error && (
        <div style={styles.errorBox}>
          <ShieldAlert size={20} color="var(--danger)" />
          <span style={styles.errorText}>{error}</span>
        </div>
      )}

      {loading ? (
        <div style={styles.loading}>Chargement des alertes...</div>
      ) : alerts.length === 0 ? (
        <div style={styles.emptyState}>
          <ShieldAlert size={48} color="var(--text-muted)" style={{ marginBottom: '16px' }} />
          <p>Aucun signalement d'urgence enregistré sur la plateforme.</p>
        </div>
      ) : (
        <div style={styles.tableContainer} className="glass-card">
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Date & Heure</th>
                <th style={styles.th}>Passager</th>
                <th style={styles.th}>Chauffeur & Véhicule</th>
                <th style={styles.th}>Statut</th>
                <th style={styles.th}>Position GPS</th>
                <th style={styles.th}>Action</th>
              </tr>
            </thead>
            <tbody>
              {alerts.map((alert) => (
                <tr key={alert.id} style={alert.isActive ? styles.activeRow : styles.row}>
                  <td style={styles.td}>
                    <div style={styles.cellWithIcon}>
                      <Clock size={16} color="#64748b" />
                      <span style={{ fontWeight: '500' }}>{formatDate(alert.alertTime)}</span>
                    </div>
                  </td>
                  <td style={styles.td}>
                    <div style={styles.cellBlock}>
                      <div style={styles.primaryText}>
                        <User size={14} color="#64748b" style={{ marginRight: '6px' }} />
                        {alert.passenger?.name || 'Inconnu'}
                      </div>
                      <div style={styles.secondaryText}>
                        <Phone size={12} color="#94a3b8" style={{ marginRight: '6px' }} />
                        {alert.passenger?.phone || 'N/A'}
                      </div>
                    </div>
                  </td>
                  <td style={styles.td}>
                    <div style={styles.cellBlock}>
                      <div style={styles.primaryText}>{alert.driver?.name || alert.driverName || 'Non assigné'}</div>
                      <div style={styles.secondaryText}>{alert.driver?.licensePlate || alert.licensePlate || 'Plaque inconnue'}</div>
                    </div>
                  </td>
                  <td style={styles.td}>
                    <span 
                      style={alert.isActive ? styles.activeBadge : styles.resolvedBadge}
                    >
                      {alert.isActive ? '🔴 ACTIVE' : `⚪ ${alert.status}`}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <div style={styles.cellWithIcon}>
                      <MapPin size={16} color={alert.isActive ? '#ba1a1a' : '#64748b'} />
                      <a
                        href={`https://maps.google.com/?q=${alert.latitude},${alert.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={alert.isActive ? styles.mapLinkActive : styles.mapLink}
                        onClick={(e) => e.stopPropagation()}
                      >
                        Localiser
                      </a>
                    </div>
                  </td>
                  <td style={styles.td}>
                    <button 
                      onClick={() => handleOpenAlert(alert)}
                      style={styles.detailsBtn}
                    >
                      Inspecter
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Details & Chat Modal */}
      {selectedAlert && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent} className="glass-card">
            {/* Modal Header */}
            <div style={styles.modalHeader}>
              <div style={styles.modalTitleArea}>
                <AlertTriangle size={24} color="#ba1a1a" />
                <div>
                  <h2 style={styles.modalTitle}>Alerte SOS : {selectedAlert.passenger?.name}</h2>
                  <span style={styles.modalSubtitle}>Course ID: {selectedAlert.rideId} • Déclenchée le {formatDate(selectedAlert.alertTime)}</span>
                </div>
              </div>
              <button onClick={handleCloseAlert} style={styles.closeBtn}>
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div style={styles.modalBody}>
              {/* Left Column: Details & Status */}
              <div style={styles.detailsCol}>
                {/* Status Card */}
                <div style={styles.infoCard}>
                  <h3 style={styles.cardSectionTitle}>Statut de l'Alerte</h3>
                  <div style={styles.statusControlGroup}>
                    <select
                      value={statusValue}
                      onChange={(e) => handleStatusChange(e.target.value)}
                      style={styles.statusSelect}
                    >
                      <option value="TRIGGERED">SIGNALÉ (TRIGGERED)</option>
                      <option value="INVESTIGATING">EN COURS D'INVESTIGATION (INVESTIGATING)</option>
                      <option value="SMS_SENT">SMS ENVOYÉ (SMS_SENT)</option>
                      <option value="SMS_FAILED">ÉCHEC SMS (SMS_FAILED)</option>
                      <option value="RESOLVED">RÉSOLU (RESOLVED)</option>
                    </select>
                    <span style={selectedAlert.isActive ? styles.activeStatusLabel : styles.resolvedStatusLabel}>
                      {selectedAlert.isActive ? '🔴 En cours de traitement' : '⚪ Archivée'}
                    </span>
                  </div>
                </div>

                {/* Passenger Info Card */}
                <div style={styles.infoCard}>
                  <h3 style={styles.cardSectionTitle}>Fiche Client (Passager)</h3>
                  {selectedAlert.passenger ? (
                    <div style={styles.infoGrid}>
                      <div style={styles.infoItem}>
                        <span style={styles.infoLabel}>Nom complet</span>
                        <span style={styles.infoValue}>{selectedAlert.passenger.name}</span>
                      </div>
                      <div style={styles.infoItem}>
                        <span style={styles.infoLabel}>Téléphone</span>
                        <span style={styles.infoValue}>
                          <Phone size={12} style={{ marginRight: '4px' }} />
                          {selectedAlert.passenger.phone || 'Non renseigné'}
                        </span>
                      </div>
                      <div style={styles.infoItem}>
                        <span style={styles.infoLabel}>Email</span>
                        <span style={styles.infoValue}>
                          <Mail size={12} style={{ marginRight: '4px' }} />
                          {selectedAlert.passenger.email || 'N/A'}
                        </span>
                      </div>
                      <div style={styles.infoItem}>
                        <span style={styles.infoLabel}>Note passager</span>
                        <span style={styles.infoValue}>
                          <Star size={12} fill="#f59e0b" color="#f59e0b" style={{ marginRight: '4px' }} />
                          {selectedAlert.passenger.rating ? `${selectedAlert.passenger.rating}/5` : 'Nouveau'}
                        </span>
                      </div>
                      <div style={{ ...styles.infoItem, gridColumn: 'span 2', borderTop: '0.5px solid var(--border-color)', paddingTop: '8px', marginTop: '4px' }}>
                        <span style={styles.infoLabel}>Contact d'urgence (SOS)</span>
                        <span style={{ ...styles.infoValue, color: '#ba1a1a', fontWeight: '600' }}>
                          {selectedAlert.passenger.emergencyContactName || 'Aucun contact enregistré'} 
                          {selectedAlert.passenger.emergencyContactPhone ? ` (${selectedAlert.passenger.emergencyContactPhone})` : ''}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <p style={styles.noInfoText}>Données passager indisponibles.</p>
                  )}
                </div>

                {/* Driver Info Card */}
                <div style={styles.infoCard}>
                  <h3 style={styles.cardSectionTitle}>Fiche Chauffeur & Véhicule</h3>
                  {selectedAlert.driver ? (
                    <div style={styles.infoGrid}>
                      <div style={styles.infoItem}>
                        <span style={styles.infoLabel}>Nom complet</span>
                        <span style={styles.infoValue}>{selectedAlert.driver.name}</span>
                      </div>
                      <div style={styles.infoItem}>
                        <span style={styles.infoLabel}>Téléphone</span>
                        <span style={styles.infoValue}>
                          <Phone size={12} style={{ marginRight: '4px' }} />
                          {selectedAlert.driver.phone || 'Non renseigné'}
                        </span>
                      </div>
                      <div style={styles.infoItem}>
                        <span style={styles.infoLabel}>Véhicule</span>
                        <span style={styles.infoValue}>{selectedAlert.driver.vehicleBrand} {selectedAlert.driver.vehicleModel}</span>
                      </div>
                      <div style={styles.infoItem}>
                        <span style={styles.infoLabel}>Plaque d'immatriculation</span>
                        <span style={{ ...styles.infoValue, fontWeight: '600', color: 'var(--primary)' }}>{selectedAlert.driver.licensePlate}</span>
                      </div>
                      <div style={styles.infoItem}>
                        <span style={styles.infoLabel}>Note chauffeur</span>
                        <span style={styles.infoValue}>
                          <Star size={12} fill="#f59e0b" color="#f59e0b" style={{ marginRight: '4px' }} />
                          {selectedAlert.driver.rating ? `${selectedAlert.driver.rating}/5` : 'Nouveau'}
                        </span>
                      </div>
                      <div style={styles.infoItem}>
                        <span style={styles.infoLabel}>Email</span>
                        <span style={styles.infoValue}>{selectedAlert.driver.email}</span>
                      </div>
                    </div>
                  ) : (
                    <div style={styles.infoGrid}>
                      <div style={styles.infoItem}>
                        <span style={styles.infoLabel}>Chauffeur</span>
                        <span style={styles.infoValue}>{selectedAlert.driverName || 'Non assigné'}</span>
                      </div>
                      <div style={styles.infoItem}>
                        <span style={styles.infoLabel}>Plaque</span>
                        <span style={styles.infoValue}>{selectedAlert.licensePlate || 'N/A'}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Column: Chat Window */}
              <div style={styles.chatCol}>
                <div style={styles.chatHeader}>
                  <MessageSquare size={16} color="var(--primary)" />
                  <span style={styles.chatTitle}>Messagerie d'assistance directe (Client)</span>
                </div>

                <div style={styles.messagesContainer}>
                  {chatLoading && chatMessages.length === 0 ? (
                    <div style={styles.chatLoadingText}>Chargement de la conversation...</div>
                  ) : chatMessages.length === 0 ? (
                    <div style={styles.chatEmptyText}>
                      Aucun message échangé. Envoyez un message d'assistance ci-dessous pour entrer en contact direct avec le passager.
                    </div>
                  ) : (
                    chatMessages.map((msg) => {
                      const isSupport = msg.authorType === 'ADMIN' || msg.senderRole === 'SUPPORT';
                      const senderRoleName = 
                        isSupport ? 'Opérateur Safe' :
                        (msg.authorType === 'CLIENT' || msg.senderRole === 'PASSENGER') ? 'Passager' :
                        (msg.authorType === 'SYSTEM') ? 'Système' : 'Chauffeur';
                      const timeField = msg.createdAt || msg.sentAt;
                      return (
                        <div 
                          key={msg.id} 
                          style={{
                            ...styles.messageWrapper,
                            justifyContent: isSupport ? 'flex-end' : 'flex-start'
                          }}
                        >
                          <div 
                            style={{
                              ...styles.messageBubble,
                              backgroundColor: isSupport ? 'var(--primary)' : '#f1f5f9',
                              color: isSupport ? 'white' : 'var(--text-primary)',
                              borderRadius: isSupport ? '12px 12px 2px 12px' : '12px 12px 12px 2px'
                            }}
                          >
                            <span style={styles.senderLabel}>
                              {senderRoleName}
                            </span>
                            <p style={styles.messageTextContent}>{msg.content}</p>
                            <span style={{
                              ...styles.messageTime,
                              color: isSupport ? 'rgba(255,255,255,0.7)' : 'var(--text-muted)'
                            }}>
                              {timeField ? formatDate(timeField).split(' ')[1] || '' : ''}
                            </span>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={chatEndRef} />
                </div>

                {/* Send Input */}
                <form onSubmit={handleSendMessage} style={styles.sendForm}>
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Écrire un message d'aide au client..."
                    style={styles.chatInput}
                    disabled={sendingMessage}
                  />
                  <button 
                    type="submit" 
                    style={styles.sendBtn}
                    disabled={sendingMessage || !newMessage.trim()}
                  >
                    <Send size={16} />
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    animation: 'fadeIn 0.4s ease',
    height: 'calc(100vh - 120px)',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '16px',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: '600',
    color: 'var(--text-primary)',
  },
  subtitle: {
    fontSize: '0.875rem',
    color: 'var(--text-secondary)',
    marginTop: '4px',
  },
  refreshBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  errorBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    backgroundColor: 'var(--danger-bg)',
    border: '0.5px solid var(--danger-border)',
    borderRadius: 'var(--radius-sm)',
  },
  errorText: {
    fontSize: '0.9rem',
    color: 'var(--danger)',
    fontWeight: '500',
  },
  loading: {
    textAlign: 'center',
    padding: '3rem',
    color: 'var(--text-secondary)',
  },
  emptyState: {
    textAlign: 'center',
    padding: '5rem 2rem',
    backgroundColor: 'white',
    borderRadius: 'var(--radius-main)',
    border: '0.5px solid var(--border-color)',
    color: 'var(--text-secondary)',
  },
  tableContainer: {
    backgroundColor: 'white',
    borderRadius: 'var(--radius-main)',
    overflowY: 'auto',
    flex: 1,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    padding: '16px 24px',
    backgroundColor: '#f8fafc',
    color: '#475569',
    fontWeight: '600',
    fontSize: '0.85rem',
    textAlign: 'left',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid #e2e8f0',
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  td: {
    padding: '16px 24px',
    borderBottom: '1px solid #e2e8f0',
    verticalAlign: 'middle',
    fontSize: '0.9rem',
  },
  row: {
    transition: 'background-color 0.2s',
  },
  activeRow: {
    backgroundColor: '#fff5f5',
    transition: 'background-color 0.2s',
  },
  cellWithIcon: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  cellBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  primaryText: {
    fontWeight: '500',
    color: 'var(--text-primary)',
    display: 'flex',
    alignItems: 'center',
  },
  secondaryText: {
    fontSize: '0.8rem',
    color: 'var(--text-muted)',
    display: 'flex',
    alignItems: 'center',
  },
  activeBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: '9999px',
    backgroundColor: '#fee2e2',
    color: '#ba1a1a',
    fontSize: '0.75rem',
    fontWeight: '700',
    animation: 'pulseBg 2s infinite ease-in-out',
    border: '1px solid #fecaca',
  },
  resolvedBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: '9999px',
    backgroundColor: '#f1f5f9',
    color: '#64748b',
    fontSize: '0.75rem',
    fontWeight: '600',
    border: '1px solid #cbd5e1',
  },
  mapLink: {
    color: '#0052ff',
    textDecoration: 'none',
    fontWeight: '500',
  },
  mapLinkActive: {
    color: '#ba1a1a',
    textDecoration: 'none',
    fontWeight: '600',
  },
  detailsBtn: {
    backgroundColor: 'var(--bg-primary)',
    border: '0.5px solid var(--border-color)',
    borderRadius: 'var(--radius-sm)',
    padding: '6px 12px',
    cursor: 'pointer',
    fontSize: '0.8rem',
    fontWeight: '600',
    color: 'var(--text-primary)',
    transition: 'all 0.2s',
    '&:hover': {
      backgroundColor: 'var(--bg-tertiary)'
    }
  },

  // Modal styling
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.4)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
    padding: '24px',
  },
  modalContent: {
    backgroundColor: 'white',
    width: '100%',
    maxWidth: '1000px',
    height: '80vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    borderRadius: '16px',
    overflow: 'hidden',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 24px',
    borderBottom: '1px solid var(--border-color)',
  },
  modalTitleArea: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
  },
  modalTitle: {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: 'var(--text-primary)',
  },
  modalSubtitle: {
    fontSize: '0.75rem',
    color: 'var(--text-muted)',
    marginTop: '2px',
    display: 'block',
  },
  closeBtn: {
    backgroundColor: 'transparent',
    border: 'none',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    padding: '4px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      backgroundColor: 'var(--bg-primary)'
    }
  },
  modalBody: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
  },
  detailsCol: {
    width: '45%',
    borderRight: '1px solid var(--border-color)',
    padding: '24px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    backgroundColor: '#fafafa',
  },
  infoCard: {
    backgroundColor: 'white',
    border: '0.5px solid var(--border-color)',
    borderRadius: '12px',
    padding: '16px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.01)',
  },
  cardSectionTitle: {
    fontSize: '0.85rem',
    fontWeight: '600',
    color: '#475569',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '12px',
    borderBottom: '0.5px solid var(--border-color)',
    paddingBottom: '8px',
  },
  statusControlGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  },
  statusSelect: {
    padding: '8px 12px',
    borderRadius: '8px',
    border: '0.5px solid var(--border-color)',
    fontSize: '0.85rem',
    fontWeight: '500',
    backgroundColor: 'white',
    outline: 'none',
    color: 'var(--text-primary)',
  },
  activeStatusLabel: {
    fontSize: '0.8rem',
    color: '#ba1a1a',
    fontWeight: '700',
    animation: 'pulseBg 2s infinite ease-in-out',
    padding: '4px 8px',
    borderRadius: '6px',
  },
  resolvedStatusLabel: {
    fontSize: '0.8rem',
    color: '#64748b',
    fontWeight: '600',
    backgroundColor: '#f1f5f9',
    padding: '4px 8px',
    borderRadius: '6px',
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
  },
  infoItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  infoLabel: {
    fontSize: '0.75rem',
    color: 'var(--text-muted)',
  },
  infoValue: {
    fontSize: '0.85rem',
    fontWeight: '500',
    color: 'var(--text-primary)',
    display: 'flex',
    alignItems: 'center',
  },
  noInfoText: {
    fontSize: '0.85rem',
    color: 'var(--text-muted)',
    fontStyle: 'italic',
  },

  // Chat window styling
  chatCol: {
    width: '55%',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  chatHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '16px 24px',
    borderBottom: '1px solid var(--border-color)',
    backgroundColor: '#fff',
  },
  chatTitle: {
    fontSize: '0.9rem',
    fontWeight: '600',
    color: 'var(--text-primary)',
  },
  messagesContainer: {
    flex: 1,
    padding: '24px',
    overflowY: 'auto',
    backgroundColor: '#f8fafc',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  chatLoadingText: {
    textAlign: 'center',
    color: 'var(--text-muted)',
    fontSize: '0.85rem',
    marginTop: '20px',
  },
  chatEmptyText: {
    textAlign: 'center',
    color: 'var(--text-muted)',
    fontSize: '0.85rem',
    padding: '32px 16px',
    fontStyle: 'italic',
    lineHeight: '1.4',
  },
  messageWrapper: {
    display: 'flex',
    width: '100%',
  },
  messageBubble: {
    maxWidth: '75%',
    padding: '10px 14px',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
  },
  senderLabel: {
    fontSize: '0.7rem',
    fontWeight: '600',
    marginBottom: '2px',
    display: 'block',
    opacity: 0.8,
  },
  messageTextContent: {
    fontSize: '0.85rem',
    lineHeight: '1.4',
    wordBreak: 'break-word',
  },
  messageTime: {
    fontSize: '0.65rem',
    float: 'right',
    marginTop: '4px',
  },
  sendForm: {
    display: 'flex',
    padding: '16px 24px',
    borderTop: '1px solid var(--border-color)',
    backgroundColor: 'white',
    gap: '12px',
  },
  chatInput: {
    flex: 1,
    height: '42px',
    borderRadius: '21px',
    border: '0.5px solid var(--border-color)',
    backgroundColor: 'var(--bg-primary)',
    paddingLeft: '20px',
    paddingRight: '20px',
    fontSize: '0.9rem',
    outline: 'none',
    transition: 'all 0.2s',
    '&:focus': {
      borderColor: 'var(--primary)',
      backgroundColor: 'white',
    }
  },
  sendBtn: {
    width: '42px',
    height: '42px',
    borderRadius: '50%',
    backgroundColor: 'var(--primary)',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'transform 0.1s, opacity 0.2s',
    boxShadow: '0 2px 4px rgba(0, 82, 255, 0.2)',
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    '&:active': {
      transform: 'scale(0.95)'
    }
  }
};

export default Emergencies;
